/* eslint-disable no-restricted-globals */
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from "@mui/material/Paper";
import React from "react";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import axios from "axios";
import { get } from "lodash";
import MidjourneyView from "app/midjourney/midjourneyView";

const MJ_BOT_ID = "936929561302675456";
const POLL_INTERVAL_MS = 5000;
const INITIAL_DELAY_MS = 15000;
const MAX_ATTEMPTS = 24;

const MidjourneyContainer = () => {
  const [prompt, setPrompt] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<any>(null);
  const [history, setHistory] = React.useState<any[]>([]);
  const [error, setError] = React.useState<string>("");
  const [status, setStatus] = React.useState<string>("");
  const [channelId, setChannelId] = React.useState<string>("");
  const pollingRef = React.useRef<NodeJS.Timer | null>(null);
  const delayRef = React.useRef<NodeJS.Timeout | null>(null);

  const getBaseUrl = () => {
    const apiHost = process.env.REACT_APP_API_HOST;
    if (apiHost) {
      return `${apiHost}/copilot/genesys/midjourney`;
    }
    let baseUrl = `//${window.location.hostname}/copilot/genesys/midjourney`;
    if (window.location.port) {
      baseUrl = `//${window.location.hostname}:${window.location.port}/copilot/genesys/midjourney`;
    }
    return baseUrl;
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (delayRef.current) {
      clearTimeout(delayRef.current);
      delayRef.current = null;
    }
  };

  const releaseCurrentChannel = (releaseId?: string) => {
    const id = releaseId || channelId;
    if (!id) return;
    const baseUrl = getBaseUrl();
    axios.post(`${baseUrl}/prompt/release`, { channelId: id }).catch(() => {});
  };

  const findCompletedImage = (data: any, searchPrompt: string) => {
    // Method 1: postMessage (batchId tracking found a new message)
    const pm = get(data, "postMessage");
    if (pm && get(pm, "attachments.length")) {
      const content = get(pm, "content", "");
      if (!content.includes("%)")) {
        return pm;
      }
    }

    // Method 2: scan results for completed image matching our prompt
    if (!searchPrompt) return null;
    const results = get(data, "result", []);
    for (const r of results) {
      const content: string = get(r, "content", "");
      const authorId = get(r, "author.id");
      const atts = get(r, "attachments", []);
      const isMJ = authorId === MJ_BOT_ID;
      const hasImage = atts.length > 0;
      const isComplete = content.includes("(fast)") || content.includes("(relaxed)") || content.includes("(turbo)");
      const notProgress = !content.includes("%)");
      const matchesPrompt = content.toLowerCase().includes(searchPrompt.toLowerCase());
      if (isMJ && hasImage && isComplete && notProgress && matchesPrompt) {
        return r;
      }
    }
    return null;
  };

  const pollForResult = (batchId: string, searchPrompt: string, pollChannelId?: string) => {
    setStatus("Waiting for Midjourney to start generating...");
    let attempts = 0;

    delayRef.current = setTimeout(() => {
      setStatus("Checking for generated image...");
      pollingRef.current = setInterval(async () => {
        try {
          attempts++;
          if (attempts > MAX_ATTEMPTS) {
            stopPolling();
            releaseCurrentChannel(pollChannelId);
            setStatus("Timed out waiting for generation");
            setLoading(false);
            return;
          }
          const baseUrl = getBaseUrl();
          const params: any = { batchId };
          if (pollChannelId) params.channelId = pollChannelId;
          const response = await axios.get(`${baseUrl}/getChannelMessages`, {
            params,
          });
          const data = get(response, "data");
          console.log("poll", attempts, data);

          const completed = findCompletedImage(data, searchPrompt);
          if (completed) {
            stopPolling();
            setResult(completed);
            setStatus("Completed");
            setLoading(false);
          } else {
            setStatus(`Generating... (attempt ${attempts}/${MAX_ATTEMPTS})`);
          }
        } catch (err: any) {
          console.error("poll error", err);
          stopPolling();
          releaseCurrentChannel(pollChannelId);
          setError(get(err, "response.data.error") || err.message || "Polling failed");
          setLoading(false);
        }
      }, POLL_INTERVAL_MS);
    }, INITIAL_DELAY_MS);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    releaseCurrentChannel();
    setLoading(true);
    setError("");
    if (result) {
      setHistory((prev: any[]) => [result, ...prev]);
    }
    setResult(null);
    setStatus("Sending prompt...");
    try {
      const baseUrl = getBaseUrl();
      const response = await axios.post(`${baseUrl}/prompt/start`, { prompt });
      const data = get(response, "data");
      console.log("prompt start result", data);
      const batchId = get(data, "batchId");
      const respChannelId = get(data, "channelId");
      const httpStatus = get(data, "status");
      if (respChannelId) setChannelId(respChannelId);
      if (batchId && httpStatus === 204) {
        setStatus("Prompt accepted by Midjourney, waiting for generation...");
        pollForResult(batchId, prompt, respChannelId);
      } else {
        setStatus("Unexpected response");
        setError(`Unexpected status: ${httpStatus}`);
        setLoading(false);
      }
    } catch (err: any) {
      console.error("generate error", err);
      setError(get(err, "response.data.error") || err.message || "Failed to generate");
      setLoading(false);
      setStatus("");
    }
  };

  const handleAction = async (messageId: string, customId: string) => {
    setLoading(true);
    setError("");
    if (result) {
      setHistory((prev: any[]) => [result, ...prev]);
    }
    setResult(null);
    setStatus("Sending action...");
    try {
      const baseUrl = getBaseUrl();
      const response = await axios.post(`${baseUrl}/prompt/action`, {
        messageId, customId, channelId: channelId || undefined
      });
      const data = get(response, "data");
      console.log("prompt action result", data);
      const batchId = get(data, "batchId");
      const respChannelId = get(data, "channelId");
      const httpStatus = get(data, "status");
      if (respChannelId) setChannelId(respChannelId);
      if (batchId && httpStatus === 204) {
        setStatus("Action accepted by Midjourney, waiting for generation...");
        pollForResult(batchId, "", respChannelId || channelId);
      } else {
        setStatus("Unexpected response");
        setError(`Unexpected status: ${httpStatus}`);
        setLoading(false);
      }
    } catch (err: any) {
      console.error("action error", err);
      setError(get(err, "response.data.error") || err.message || "Failed to perform action");
      setLoading(false);
      setStatus("");
    }
  };

  const handlePromptChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
    setPrompt(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !loading) {
      handleGenerate();
    }
  };

  React.useEffect(() => {
    return () => stopPolling();
  }, []);

  return (
    <Box component="main" sx={{ display: "flex" }}>
      <Paper sx={{ justifyContent: "center", marginTop: "25px", width: "100%" }}>
        <Grid container sx={{ marginTop: "10px", padding: "10px" }}>
          <Grid item xs={12} sx={{ justifyContent: "center" }}>
            {loading ? <LinearProgress /> : <div />}
            <Typography variant="h4" component="h4">
              Midjourney Image Generator
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <TextField
              onChange={handlePromptChange}
              onKeyDown={handleKeyDown}
              fullWidth
              multiline
              minRows={1}
              maxRows={6}
              placeholder="Enter a prompt for Midjourney..."
              value={prompt}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={4} sx={{ paddingLeft: "10px" }}>
            <Button variant="contained" onClick={handleGenerate} disabled={loading || !prompt.trim()}>
              Generate
            </Button>
          </Grid>
          <MidjourneyView
            result={result}
            loading={loading}
            error={error}
            status={status}
            history={history}
            onAction={handleAction}
          />
        </Grid>
      </Paper>
    </Box>
  );
};

export default MidjourneyContainer;
